import { asyncSome } from '../../utils/asyncSome';

interface ICheckOnlineParams {
  /** Период для поллинга */
  period: number;
  /** Список URL для поллинга. Если один отвалился, проверяет следующий. Если массив пустой, то считает онлайн */
  urls: string[];
  /** Коллбэк, когда вышли онлайн */
  onOnline(): void;
  /** Коллбэк, когда ушли оффлайн */
  onOffline(): void;
}

const REQUEST_TIMEOUT = 5000;

/**
 * Проверяет соединение с интернетом.
 */
export function checkOnline({ period, onOffline, onOnline, urls }: ICheckOnlineParams): () => void {
  let isOnline = true;
  let isTimeoutStopped = false;

  const checkOnlineHandler = async (url: string, resolve: (result: boolean) => void) => {
    let resolved = false;
    // Если нет ответа за время таймаута, значит оффлайн
    const timer = setTimeout(() => {
      resolved = true;
      resolve(false);
    }, REQUEST_TIMEOUT);

    try {
      // Намеренно утилизируем функцию под нативный fetch, чтобы не было зависимости от библиотек
      const online = await fetch(url, {
        // Исключаем ложные срабатывания из кэша
        cache: 'no-cache',
        // HEAD-запросы быстрее
        method: 'HEAD',
      });
      clearTimeout(timer);
      if (!resolved) {
        resolve(online.status >= 200 && online.status < 300);
      }
    } catch (err) {
      if (!resolved) {
        resolve(false);
      }
    }
  };

  const checkOnlineStatus = () => {
    return new Promise(resolve => {
      // Если нет ссылок, считаем всегда онлайн (для разработки удобно)
      if (!urls.length) {
        resolve(true);
      }

      // Проверяем все ссылки из списка по очереди, ждем первую ответившую
      asyncSome(urls, url => new Promise(resolveAsyncSome => checkOnlineHandler(url, resolveAsyncSome))).then(resolve);
    });
  };

  const pollOnlineStatus = () => {
    setTimeout(async () => {
      // Так избегаем гонки запроса и таймаута
      if (isTimeoutStopped) {
        return;
      }

      const result = await checkOnlineStatus();

      // Событие должно стрелять один раз
      if (result && !isOnline) {
        isOnline = true;
        onOnline();
      }

      // Событие должно стрелять один раз
      if (!result && isOnline) {
        isOnline = false;
        onOffline();
      }

      pollOnlineStatus();
    }, period);
  };

  pollOnlineStatus();

  return () => {
    isTimeoutStopped = true;
  };
}
